import type { ApiClient } from "../../../app/api/client";
import type { LocalizedText } from "../../../app/i18n/i18n";
import { BLOCK_KEY_FIRST_DUPLICATE_INDEX, BLOCK_SHEET, FOOTER_DEFAULT_COLUMN_COUNT, FOOTER_DEFAULT_ROW_COUNT, LOGO_DEFAULT_HEIGHT_MM, LOGO_DEFAULT_HEIGHT_PX } from "./documentAppearanceConstants";

/** Muss zu DocumentLogoPosition im Backend passen. */
export const LOGO_POSITION = { left: 0, center: 1, right: 2 } as const;

/** Muss zu DocumentBlockTypes.Sender* im Backend passen. */
export const SENDER_PLACEMENT = { above: "above", right: "right" } as const;

/** Muss zu DocumentFooterLayout im Backend passen. */
export const FOOTER_LAYOUT = { columns: 0, rows: 1 } as const;

/** Muss zu DocumentTextAlignment im Backend passen. */
export const TEXT_ALIGNMENT = { left: 0, center: 1, right: 2 } as const;

/** Muss zu DocumentBlockDataSources im Backend passen. */
export const BLOCK_DATA_SOURCE = { studio: "studio", tenant: "tenant", companyAndStudio: "company-and-studio" } as const;

/** Muss zu DocumentFooterItems im Backend passen. */
export const FOOTER_ITEMS = {
  studioName: "studio.name",
  studioAddress: "studio.address",
  studioPhone: "studio.phone",
  studioEmail: "studio.email",
  studioWebsite: "studio.website",
  tenantName: "tenant.name",
  tenantRepresentatives: "tenant.representatives",
  tenantSupervisoryBoardChair: "tenant.supervisory-board-chair",
  tenantAddress: "tenant.address",
  tenantPhone: "tenant.phone",
  tenantEmail: "tenant.email",
  tenantWebsite: "tenant.website",
  tenantRegisteredOffice: "tenant.registered-office",
  tenantRegister: "tenant.register",
  tenantTaxNumber: "tenant.tax-number",
  tenantVatId: "tenant.vat-id",
} as const;

/**
 * Muss zu DocumentBlockTypes im Backend passen.
 *
 * Einen Datenbaustein gibt es hier bewusst nicht: Wo die Aufstellung eines Vorgangs steht, setzt
 * eine Blockvariable im Text fest — ein fester Platz im Format würde jede Formulierung darum
 * herum bauen müssen.
 */
export const BLOCK_TYPES = {
  logo: "logo",
  sender: "sender",
  address: "address",
  date: "date",
  subject: "subject",
  salutation: "salutation",
  introduction: "introduction",
  notice: "notice",
  closing: "closing",
  footer: "footer",
  /**
   * Die Seitenangabe — „Seite 1 von 3". Ein eigener Baustein: Sie hat einen eigenen Wortlaut, eine
   * eigene Seitenangabe und ist unabhängig davon vorhanden, ob es einen Fußbereich gibt. Gezeichnet
   * wird sie wie der Fuß auf jede Seite.
   */
  pageNumber: "pageNumber",
  text: "text",
  divider: "divider",
  spacer: "spacer",
} as const;

export type DocumentBlockType = (typeof BLOCK_TYPES)[keyof typeof BLOCK_TYPES];

/**
 * Woran ein Baustein im Brief hängt. `top` ist der Textfluss — der Regelfall; `bottom` der
 * Blattfuß: Der Baustein steht dann unmittelbar über dem Fußbereich, unabhängig davon, wo der
 * Text endet.
 */
export const BLOCK_ANCHOR = {
  top: "top",
  bottom: "bottom",
} as const;

/**
 * Bausteine, die sich am Blattfuß verankern lassen — die freien. Anschriftfeld, Betreff oder Gruß
 * haben ihren Platz im Satzspiegel, und der Fußstempel zeichnet keine Datenaufstellungen.
 */
export const BOTTOM_ANCHORABLE_BLOCK_TYPES: DocumentBlockType[] = [
  BLOCK_TYPES.text,
  BLOCK_TYPES.divider,
  BLOCK_TYPES.spacer,
];

/** Ob der Baustein im Brief am Blattfuß hängt. */
export function isBottomAnchored(block: { anchor?: string | null; type: DocumentBlockType }): boolean {
  return block.anchor === BLOCK_ANCHOR.bottom && BOTTOM_ANCHORABLE_BLOCK_TYPES.includes(block.type);
}

/** Bausteine, die im gesamten Dokument nur 1x vorkommen dürfen und exklusiv auf Seite 1 / im Textfluss stehen. */
export const GLOBAL_PAGE1_BLOCK_TYPES: DocumentBlockType[] = [
  BLOCK_TYPES.address,
  BLOCK_TYPES.date,
  BLOCK_TYPES.subject,
  BLOCK_TYPES.salutation,
  BLOCK_TYPES.introduction,
  BLOCK_TYPES.notice,
  BLOCK_TYPES.closing,
];

/** Bausteine, die je Seite höchstens 1x vorkommen dürfen (Seitenanker für Kopf und Fuß auf Seite 1 und/oder Folgeseite 2). */
export const PER_PAGE_BLOCK_TYPES: DocumentBlockType[] = [
  BLOCK_TYPES.logo,
  BLOCK_TYPES.sender,
  BLOCK_TYPES.footer,
  BLOCK_TYPES.pageNumber,
];

/** Bausteine, die frei hinzugefügt und mehrfach verwendet werden dürfen. */
export const REPEATABLE_BLOCK_TYPES: DocumentBlockType[] = [
  BLOCK_TYPES.text,
  BLOCK_TYPES.divider,
  BLOCK_TYPES.spacer,
];

/** Alle Bausteine mit Eindeutigkeitsregeln. */
export const UNIQUE_BLOCK_TYPES: DocumentBlockType[] = [
  ...GLOBAL_PAGE1_BLOCK_TYPES,
  ...PER_PAGE_BLOCK_TYPES,
];

export const BLOCK_LABELS: Record<DocumentBlockType, LocalizedText> = {
  logo: "Logo",
  sender: "Sender line",
  address: "Address field",
  date: "Date line",
  subject: "Subject",
  salutation: "Salutation",
  introduction: "Main text",
  notice: "Notice text",
  closing: "Closing formula",
  footer: "Footer",
  pageNumber: "Page number",
  text: "Fixed text",
  divider: "Divider",
  spacer: "Spacing",
};

export type DocumentLayoutBlock = {
  id: string;
  /**
   * Sprechender Schlüssel, eindeutig innerhalb eines Dokuments. Ein abgeleitetes Dokument
   * überschreibt genau diesen Baustein darüber — die `id` taugt dafür nicht, sie wechselt beim
   * Neuanlegen. Der Server vergibt fehlende Schlüssel beim Lesen aus dem Typ.
   */
  key?: string | null;
  type: DocumentBlockType;
  enabled: boolean;
  /**
   * Woran der Baustein im Brief hängt: `top` im Textfluss (Standard), `bottom` am Blattfuß —
   * unmittelbar über dem Fußbereich, unabhängig davon, wo der Text endet. Nur für die freien
   * Bausteine (Text, Trennlinie, Abstand); die E-Mail kennt keinen Blattfuß.
   */
  anchor?: string | null;
  /** Nur beim Textbaustein. */
  text?: string | null;
  /** Nur beim Logo: Lage im Brief. */
  position?: number | null;
  /** Nur beim Logo: Lage im E-Mail-Kopf. */
  emailPosition?: number | null;
  /**
   * Logo-Höhe bzw. Höhe eines Abstandsbausteins im Brief, in Millimetern. Beim Logo ist das die
   * eingestellte Größe; die Breite folgt dem Seitenverhältnis des Bildes.
   */
  heightMm?: number | null;
  /** Höhe eines Abstandsbausteins bzw. des Logos in der E-Mail, in Pixeln. */
  heightPx?: number | null;
  /** Nur beim E-Mail-Logo: den Kopf mit der erkannten Randfarbe ausfüllen. */
  fillBackground?: boolean | null;
  /** Nur beim Fußbereich. */
  footerLayout?: number | null;
  footerColumns?: number | null;
  footerRows?: number | null;
  /** Position des gesamten zeilenweisen Fußblocks, unabhängig von der Textausrichtung. */
  footerBlockAlignment?: number | null;
  footerTextColor?: string | null;
  footerBackgroundColor?: string | null;
  /** Auf welchen Seiten der Baustein steht: 0/leer = alle, 1 = nur die erste, 2 = ab der zweiten. */
  pages?: number | null;
  /** Ab welcher Seite die Seitenangabe steht; 0 = gar nicht, 1 = jede Seite, 2 = ab der zweiten. */
  pageNumberFromPage?: number | null;
  /** Wortlaut der Seitenangabe mit {{pageNumber}} und {{pageCount}}; leer = Standardsatz. */
  pageNumberText?: string | null;
  footerSectionOrder?: string[] | null;
  /**
   * Ausgewählte Inhalte je Fußgruppe. Layoutunabhängig: Beim zeilenweisen Fuß wird jede Gruppe
   * eine Zeile, beim spaltenweisen eine Spalte.
   */
  footerLineItems?: string[][] | null;
  /**
   * Der geschriebene Fußtext, ein Eintrag je Gruppe: spaltenweise eine Spalte, zeilenweise genau
   * ein Eintrag mit Zeilenumbrüchen. Variablen werden beim Rendern eingesetzt. Solange er fehlt,
   * gilt weiter `footerLineItems` — ein Bestandsformat sieht unverändert aus, bis es gespeichert
   * wird.
   */
  footerTexts?: string[] | null;
  footerSeparator?: string | null;
  footerSeparatorColor?: string | null;
  footerSeparatorBold?: boolean | null;
  footerSeparatorItalic?: boolean | null;
  footerSeparatorUnderline?: boolean | null;
  /** Optionale Farben des gesamten Bausteins. */
  textColor?: string | null;
  backgroundColor?: string | null;
  /** Waagerechte Ausrichtung des Texts im Baustein. */
  textAlignment?: number | null;
  /** Schriftfamilie des Bausteins; leer = Grundschriftart des Formats. */
  fontFamily?: string | null;
  /** Schriftgröße des Bausteins in Punkt; leer = Grundschriftgröße des Formats. */
  fontSizePt?: number | null;
  /**
   * Auszeichnung des ganzen Bausteins. Sie steht hier, weil sie in jedem Schreiben gleich ist —
   * die Auszeichnung einzelner Wörter innerhalb eines Satzes gehört dagegen zum Text.
   */
  bold?: boolean | null;
  italic?: boolean | null;
  underline?: boolean | null;
  /** Beim Absender Studio oder Mandant; im Fußbereich zusätzlich Firma und Studio kombiniert. */
  dataSource?: string | null;
  /** Nur bei der Absenderzeile: über dem Anschriftenfeld oder rechts oben. */
  placement?: string | null;
  /** Nur beim Betreff: „Betreff:" vor den Text setzen. */
  showLabel?: boolean | null;
  /** Zeilenabstand des Bausteins; leer = Vorgabe des Formats. */
  lineHeight?: number | null;
  /** Absatzabstand unter Absätzen dieses Bausteins im Brief, in Punkt (pt); leer = Vorgabe des Formats. */
  paragraphSpacingPt?: number | null;
  /** Absatzabstand unter Absätzen dieses Bausteins im Brief, in Millimetern; leer = Vorgabe des Formats. */
  paragraphSpacingMm?: number | null;
  /** Absatzabstand unter Absätzen dieses Bausteins in der E-Mail, in Pixeln; leer = Vorgabe des Formats. */
  paragraphSpacingPx?: number | null;
  /** Abstand unter dem Baustein im Brief, in Millimetern; leer = Standardabstand. */
  spacingMm?: number | null;
  /** Abstand unter dem Baustein in der E-Mail, in Pixeln; leer = Standardabstand. */
  spacingPx?: number | null;
  /** Abstand vor dem Baustein im Brief, in Millimetern. */
  spacingBeforeMm?: number | null;
  /** Abstand vor dem Baustein in der E-Mail, in Pixeln. */
  spacingBeforePx?: number | null;
  /** Stärke einer Trennlinie im Brief in Punkt (pt); Standard ist 0.5 pt. */
  borderWidthPt?: number | null;
  /** Stärke einer Trennlinie in der E-Mail in Pixeln (px); Standard ist 1 px. */
  borderWidthPx?: number | null;
  /** Breite / Länge des Bausteins in Prozent (10-100); Standard ist 100 %. */
  widthPercent?: number | null;
};

/** Gestaltung der E-Mail-Hülle außerhalb der Inhaltsbausteine. */
export type DocumentEmailAppearance = {
  /** Hintergrund außerhalb der Inhaltskarte. */
  pageBackgroundColor?: string | null;
  /** Hintergrund der Inhaltskarte. */
  contentBackgroundColor?: string | null;
  /** Standard-Schriftfarbe der E-Mail. */
  textColor?: string | null;
  /** Füllfarbe des Logo-Kopfs; leer = Logo-Randfarbe. */
  logoBackgroundColor?: string | null;
  /** Fußbereich unterhalb der Inhaltskarte. */
  footerOutsideContent?: boolean | null;
  // Schrift und Zeilenabstand der Mail — bewusst getrennt von den Blattangaben des Briefs.
  /** Schriftfamilie als vollständige Ersatzkette; leer = Vorgabe. */
  fontFamily?: string | null;
  /** Grundschriftgröße der Mail in Punkt. */
  bodyFontSizePt?: number | null;
  /** Grundschriftgröße der Mail in Pixel (Kompatibilität). */
  bodyFontSizePx?: number | null;
  /** Schriftgröße kleiner Nebenangaben in Pixel (Kompatibilität). */
  smallFontSizePx?: number | null;
  /** Zeilenabstand als Vielfaches der Schriftgröße. */
  lineHeight?: number | null;
  /** Absatzabstand der Mail in Pixeln. */
  paragraphSpacingPx?: number | null;
};

/**
 * Schrift und Blattmaße des Briefs. Sie gehören zum Format: Ein Haus, das größer setzt oder engere
 * Ränder fährt, ändert damit jedes seiner Schreiben.
 */
export type DocumentPageAppearance = {
  /** Schriftfamilie als vollständige Ersatzkette; leer = Vorgabe des Renderers. */
  fontFamily?: string | null;
  /** Grundschriftgröße in Punkt. */
  bodyFontSizePt?: number | null;
  /** Schriftgröße kleiner Nebenangaben — Fußbereich, Absenderzeile. */
  smallFontSizePt?: number | null;
  /** Globale Grund-Textfarbe des Dokuments. */
  textColor?: string | null;
  /** Zeilenabstand des Fließtexts als Vielfaches der Schriftgröße; leer = Vorgabe des Renderers. */
  lineHeight?: number | null;
  /** Absatzabstand des Briefs in Punkt (pt). */
  paragraphSpacingPt?: number | null;
  /** Absatzabstand des Briefs in Millimetern (Kompatibilität). */
  paragraphSpacingMm?: number | null;
  marginTopMm?: number | null;
  marginRightMm?: number | null;
  marginBottomMm?: number | null;
  marginLeftMm?: number | null;
  /**
   * Oberkante des Anschriftfelds ab Blattoberkante. Fest und nicht im Fluss, damit das Feld nicht
   * mit der Logohöhe wandert und das Sichtfenster des Kuverts verfehlt (DIN 5008: 45 mm).
   */
  addressFieldTopMm?: number | null;
  /**
   * Falz- und Lochmarken am linken Rand (DIN 5008: 87 mm, 148,5 mm, 192 mm). Aus, solange nichts
   * eingestellt ist — wer kuvertieren lässt, braucht sie nicht.
   */
  foldMarks?: boolean | null;
};

/** Gestaltung aller Vorgangstabellen eines Kanals dieses Erscheinungsbilds. */
export type DocumentTableTextStyle = {
  bold?: boolean | null;
  italic?: boolean | null;
  underline?: boolean | null;
  fontFamily?: string | null;
  fontSizePt?: number | null;
  color?: string | null;
  /** Hex = eigene Füllung, `transparent` = keine Füllung, leer = Tabellenstandard. */
  backgroundColor?: string | null;
  alignment?: number | null;
};

export type DocumentTableAppearance = {
  headerStyle?: DocumentTableTextStyle | null;
  cellStyle?: DocumentTableTextStyle | null;
  totalStyle?: DocumentTableTextStyle | null;
};

/**
 * Wie eine angehakte Fußangabe als geschriebener Text aussieht. Die Beschriftungen sind dieselben,
 * die der Renderer bisher fest davorgesetzt hat — nur stehen sie jetzt im Text und sind damit
 * änderbar.
 */
const FOOTER_ITEM_TEXTS: Record<string, string> = {
  [FOOTER_ITEMS.studioName]: "{{studioName}}",
  [FOOTER_ITEMS.studioAddress]: "{{studioAddress}}",
  [FOOTER_ITEMS.studioPhone]: "{{studioPhone}}",
  [FOOTER_ITEMS.studioEmail]: "{{studioEmail}}",
  [FOOTER_ITEMS.studioWebsite]: "{{studioWebsite}}",
  [FOOTER_ITEMS.tenantName]: "{{companyName}}",
  [FOOTER_ITEMS.tenantRepresentatives]: "{{companyRepresentativesLabel}}: {{companyRepresentatives}}",
  [FOOTER_ITEMS.tenantSupervisoryBoardChair]: "Vorsitz Aufsichtsrat: {{companySupervisoryBoardChair}}",
  [FOOTER_ITEMS.tenantAddress]: "{{companyAddress}}",
  [FOOTER_ITEMS.tenantPhone]: "{{companyPhone}}",
  [FOOTER_ITEMS.tenantEmail]: "{{companyEmail}}",
  [FOOTER_ITEMS.tenantWebsite]: "{{companyWebsite}}",
  [FOOTER_ITEMS.tenantRegisteredOffice]: "Sitz: {{companyCity}}",
  [FOOTER_ITEMS.tenantRegister]: "Register: {{companyRegister}}",
  [FOOTER_ITEMS.tenantTaxNumber]: "Steuernummer: {{companyTaxNumber}}",
  [FOOTER_ITEMS.tenantVatId]: "USt-IdNr.: {{companyVatId}}",
};

/**
 * Die ausgelieferte Feldauswahl je Datenquelle — muss zu `DefaultFooterLineItems` im Backend
 * passen. Ein Format, das noch gar keine Auswahl gespeichert hat, zeigt genau diese Angaben; ohne
 * sie hier stünde beim Umstieg auf den geschriebenen Fußtext ein leerer Fuß.
 */
const DEFAULT_FOOTER_LINE_ITEMS: Record<string, string[][]> = {
  [BLOCK_DATA_SOURCE.studio]: [
    [FOOTER_ITEMS.studioName],
    [FOOTER_ITEMS.studioAddress],
    [FOOTER_ITEMS.studioPhone, FOOTER_ITEMS.studioEmail, FOOTER_ITEMS.studioWebsite],
  ],
  [BLOCK_DATA_SOURCE.tenant]: [
    [FOOTER_ITEMS.tenantName, FOOTER_ITEMS.tenantRepresentatives, FOOTER_ITEMS.tenantSupervisoryBoardChair],
    [FOOTER_ITEMS.tenantAddress, FOOTER_ITEMS.tenantPhone, FOOTER_ITEMS.tenantEmail, FOOTER_ITEMS.tenantWebsite],
    [FOOTER_ITEMS.tenantRegisteredOffice, FOOTER_ITEMS.tenantRegister, FOOTER_ITEMS.tenantTaxNumber, FOOTER_ITEMS.tenantVatId],
  ],
  [BLOCK_DATA_SOURCE.companyAndStudio]: [
    [FOOTER_ITEMS.tenantName, FOOTER_ITEMS.tenantRepresentatives, FOOTER_ITEMS.tenantSupervisoryBoardChair],
    [FOOTER_ITEMS.studioName, FOOTER_ITEMS.studioAddress, FOOTER_ITEMS.studioPhone, FOOTER_ITEMS.studioEmail, FOOTER_ITEMS.studioWebsite],
    [FOOTER_ITEMS.tenantRegisteredOffice, FOOTER_ITEMS.tenantRegister, FOOTER_ITEMS.tenantTaxNumber, FOOTER_ITEMS.tenantVatId],
  ],
};

/**
 * Der Fußtext eines Formats, das noch die Feldauswahl führt: Aus jeder Gruppe wird eine Zeile aus
 * Variablen, verbunden mit dem eingestellten Trennzeichen.
 *
 * Läuft im Editor beim Öffnen, nicht auf dem Server beim Lesen: So bleibt ein Bestandsformat
 * unverändert, bis jemand es tatsächlich speichert — und wer nur hineinsieht, ändert nichts.
 */
export function footerTextsFromLineItems(block: DocumentLayoutBlock): string[] {
  const separator = (block.footerSeparator ?? "·").trim() || "·";
  const selected = (block.footerLineItems ?? []).length > 0
    ? block.footerLineItems!
    : DEFAULT_FOOTER_LINE_ITEMS[block.dataSource ?? BLOCK_DATA_SOURCE.companyAndStudio]
      ?? DEFAULT_FOOTER_LINE_ITEMS[BLOCK_DATA_SOURCE.companyAndStudio];
  const groups = selected
    .map((line) => line.map((item) => FOOTER_ITEM_TEXTS[item]).filter(Boolean).join(` ${separator} `))
    .filter((line) => line.length > 0);

  if (groups.length === 0) return [];
  // Zeilenweise ist der ganze Fuß ein Text; die Gruppen werden zu seinen Zeilen.
  return (block.footerLayout ?? FOOTER_LAYOUT.columns) === FOOTER_LAYOUT.rows
    ? [groups.join("\n")]
    : groups;
}

/**
 * Der vollständige Fußtext, nachdem eine Gruppe beschrieben wurde.
 *
 * Führt der Baustein noch die Feldauswahl, wird sie dabei **einmal vollständig** übersetzt. Ohne
 * diesen Schritt entstünde ein Fußtext, in dem nur die gerade beschriebene Gruppe steht: Sobald
 * irgendein Eintrag gesetzt ist, gilt der geschriebene Text — und alle übrigen Angaben wären fort.
 */
export function footerTextsWithGroup(
  block: Pick<DocumentLayoutBlock, "footerTexts" | "footerLineItems" | "footerLayout" | "footerSeparator" | "dataSource">,
  groupIndex: number,
  value: string
): string[] {
  const stored = block.footerTexts ?? [];
  const next = stored.length > 0 ? [...stored] : footerTextsFromLineItems(block as DocumentLayoutBlock);
  while (next.length <= groupIndex) next.push("");
  next[groupIndex] = value;
  return next;
}

/**
 * Hebt Fußbausteine, die noch die Feldauswahl führen, beim Öffnen auf den geschriebenen Fußtext.
 *
 * Der Fuß ist im Blatt beschreibbar, und beschrieben werden kann nur, was der Renderer als Gruppen
 * ausgibt — das tut er ausschließlich für den geschriebenen Text. Solange ein Format noch die
 * Feldauswahl führte, lief jede Eingabe in der Fußzeile ins Leere. Gespeichert wird die Umstellung
 * erst, wenn jemand tatsächlich speichert.
 */
export function withWritableFooter(entry: DocumentAppearanceDto): DocumentAppearanceDto {
  const lift = (blocks: DocumentLayoutBlock[]) => blocks.map((block) =>
    block.type === BLOCK_TYPES.footer && (block.footerTexts ?? []).length === 0
      ? { ...block, footerTexts: footerTextsFromLineItems(block) }
      : block);
  return { ...entry, letterBlocks: lift(entry.letterBlocks), emailBlocks: lift(entry.emailBlocks) };
}

/**
 * Die Seitenangabe ist ein regulärer Baustein in letterBlocks.
 * Gelöschte Bausteine werden beim Neuladen nicht automatisch wiederhergestellt.
 */
export function withPageNumberBlock(entry: DocumentAppearanceDto): DocumentAppearanceDto {
  return entry;
}

/** Auf welchen Seiten ein Baustein steht — muss zu `DocumentLayoutBlock.Pages` im Backend passen. */
export const BLOCK_PAGES = { all: 0, first: 1, following: 2 } as const;

/** Ob der Baustein auf der gezeigten Seite erscheint. Entspricht `ShowsOnPage` im Backend. */
export function blockShowsOnPage(block: Pick<DocumentLayoutBlock, "pages">, page: 1 | 2): boolean {
  const pages = block.pages ?? BLOCK_PAGES.all;
  if (pages === BLOCK_PAGES.first) return page === 1;
  if (pages === BLOCK_PAGES.following) return page >= BLOCK_SHEET.following;
  return true;
}

/**
 * Die Änderung, die einen Baustein auf der **gezeigten** Seite ein- oder ausblendet.
 *
 * Ohne diese Unterscheidung war das Auge seitenblind: Wer auf der Folgeseite den Fuß ausblendete,
 * verlor ihn auch auf der ersten. Steht der Baustein auf der anderen Seite weiter, schrumpft
 * deshalb seine Seitenangabe, statt ihn ganz abzuschalten — abgeschaltet wird er erst, wenn er
 * nirgends mehr steht.
 */
export function blockVisibilityPatch(
  block: Pick<DocumentLayoutBlock, "pages" | "enabled">,
  page: 1 | 2,
  visible: boolean
): Partial<DocumentLayoutBlock> {
  const other: 1 | 2 = page === BLOCK_SHEET.first ? BLOCK_SHEET.following : BLOCK_SHEET.first;
  const staysOnOther = block.enabled !== false && blockShowsOnPage(block, other);

  if (!visible) {
    return staysOnOther
      ? { enabled: true, pages: other === 1 ? BLOCK_PAGES.first : BLOCK_PAGES.following }
      : { enabled: false };
  }
  // Einblenden: Der Baustein muss auf dieser Seite erscheinen — und dort bleiben, wo er schon stand.
  return {
    enabled: true,
    pages: staysOnOther
      ? BLOCK_PAGES.all
      : page === 1 ? BLOCK_PAGES.first : BLOCK_PAGES.following,
  };
}

/**
 * Prüft, ob ein Bausteintyp auf einer bestimmten Seite (1 oder 2) noch hinzugefügt / abgelegt werden darf.
 */
export function isBlockAllowedOnPage(
  type: string,
  targetPage: 1 | 2,
  existingBlocks: DocumentLayoutBlock[],
  surface: "letter" | "email" = "letter"
): boolean {
  if (REPEATABLE_BLOCK_TYPES.includes(type as DocumentBlockType)) {
    return true;
  }

  // E-Mail hat keine Folgeseiten:
  if (surface === "email") {
    return !existingBlocks.some((b) => b.type === type && b.enabled !== false);
  }

  // Global einzigartige Seite-1-Bausteine (Anschrift, Datum, Betreff, Textfluss):
  if (GLOBAL_PAGE1_BLOCK_TYPES.includes(type as DocumentBlockType)) {
    if (targetPage === BLOCK_SHEET.following) return false;
    return !existingBlocks.some((b) => b.type === type && b.enabled !== false);
  }

  // Seiten-einzigartige Bausteine (Logo, Absender, Fuß, Seitennummer):
  if (PER_PAGE_BLOCK_TYPES.includes(type as DocumentBlockType)) {
    return !existingBlocks.some(
      (b) => b.type === type && b.enabled !== false && blockShowsOnPage(b, targetPage)
    );
  }

  return true;
}

/**
 * Ein eindeutiger Schlüsselvorschlag für einen neuen Baustein: der Typ, bei Kollision durchgezählt.
 * Dieselbe Regel wie `DocumentLayoutBlocks.EnsureKeys` im Backend, das beim Speichern das letzte
 * Wort behält.
 */
export function suggestBlockKey(type: DocumentBlockType, blocks: DocumentLayoutBlock[]): string {
  const taken = new Set(blocks.map((block) => block.key).filter(Boolean));
  if (!taken.has(type)) return type;
  for (let index = BLOCK_KEY_FIRST_DUPLICATE_INDEX; ; index++) {
    const candidate = `${type}-${index}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** Der ausgelieferte Aufbau — muss zu DocumentLayoutBlocks.Defaults im Backend passen. */
export function defaultBlocks(): DocumentLayoutBlock[] {
  return [
    { id: BLOCK_TYPES.logo, key: BLOCK_TYPES.logo, type: BLOCK_TYPES.logo, enabled: true, position: LOGO_POSITION.left, emailPosition: LOGO_POSITION.center, heightMm: LOGO_DEFAULT_HEIGHT_MM, heightPx: LOGO_DEFAULT_HEIGHT_PX, fillBackground: true },
    { id: BLOCK_TYPES.sender, key: BLOCK_TYPES.sender, type: BLOCK_TYPES.sender, enabled: true, placement: SENDER_PLACEMENT.right, dataSource: BLOCK_DATA_SOURCE.studio },
    { id: BLOCK_TYPES.address, key: BLOCK_TYPES.address, type: BLOCK_TYPES.address, enabled: true },
    { id: BLOCK_TYPES.subject, key: BLOCK_TYPES.subject, type: BLOCK_TYPES.subject, enabled: true },
    { id: BLOCK_TYPES.salutation, key: BLOCK_TYPES.salutation, type: BLOCK_TYPES.salutation, enabled: true },
    { id: BLOCK_TYPES.introduction, key: BLOCK_TYPES.introduction, type: BLOCK_TYPES.introduction, enabled: true },
    { id: BLOCK_TYPES.notice, key: BLOCK_TYPES.notice, type: BLOCK_TYPES.notice, enabled: true },
    { id: BLOCK_TYPES.closing, key: BLOCK_TYPES.closing, type: BLOCK_TYPES.closing, enabled: true },
    { id: BLOCK_TYPES.footer, key: BLOCK_TYPES.footer, type: BLOCK_TYPES.footer, enabled: true, footerLayout: FOOTER_LAYOUT.columns, footerColumns: FOOTER_DEFAULT_COLUMN_COUNT, footerRows: FOOTER_DEFAULT_ROW_COUNT, footerBlockAlignment: TEXT_ALIGNMENT.left, dataSource: BLOCK_DATA_SOURCE.companyAndStudio },
    { id: BLOCK_TYPES.pageNumber, key: BLOCK_TYPES.pageNumber, type: BLOCK_TYPES.pageNumber, enabled: true, textAlignment: TEXT_ALIGNMENT.right },
  ];
}

/**
 * Namen der Backend-Enums; die API serialisiert sie bewusst als Strings. Das Backend akzeptiert
 * zusätzlich numerische Altwerte, neue Clients senden ausschließlich diese Namen.
 */
export type WhatsAppAppearanceHeaderType = "None" | "Text" | "Image" | "Video" | "Document";
export type WhatsAppAppearanceButtonType = "QuickReply" | "Url" | "Phone";

export type WhatsAppAppearanceButtonDto = {
  type: WhatsAppAppearanceButtonType;
  label: string;
  target?: string | null;
};

export type WhatsAppAppearanceDto = {
  headerType: WhatsAppAppearanceHeaderType;
  headerText?: string | null;
  footerText?: string | null;
  buttons: WhatsAppAppearanceButtonDto[];
};

export type DocumentAppearanceDto = {
  /** Fehlt bei dem ausgelieferten Standard — daran ist er erkennbar. */
  id?: string | null;
  name?: string | null;
  /** Erlaubte Studios; leer = alle. Wie bei den Textvorlagen. */
  studioIds: string[];
  /** Dokumentkanäle des Speichervertrags. Die UI ergänzt daraus mit `appliesToWhatsApp` eine Typauswahl. */
  channel: string[];
  isActive: boolean;
  /** Unabhängiger Aufbau der Briefausgabe. */
  letterBlocks: DocumentLayoutBlock[];
  /** Unabhängiger Aufbau der E-Mail-Ausgabe. */
  emailBlocks: DocumentLayoutBlock[];
  /** Gestaltung der E-Mail-Hülle und die Platzierung des Fußbereichs. */
  emailAppearance?: DocumentEmailAppearance | null;
  /** Schrift und Blattmaße; leere Werte bedeuten die Vorgaben des Renderers. */
  pageAppearance?: DocumentPageAppearance | null;
  /** Optik aller Tabellen im Brief. */
  letterTableAppearance?: DocumentTableAppearance | null;
  /** Optik aller Tabellen in der E-Mail. */
  emailTableAppearance?: DocumentTableAppearance | null;
  /** WhatsApp-Anteil des Speichervertrags; in der UI ist WhatsApp ein Wert der gemeinsamen Typauswahl. */
  appliesToWhatsApp: boolean;
  /** WhatsApp-spezifisches Teilprofil; getrennt von Brief-/E-Mail-Bausteinen. */
  whatsAppAppearance?: WhatsAppAppearanceDto | null;
};

export type DocumentAppearancePreviewDto = {
  letterHtml: string;
  emailHtml: string;
  /** Die Folgeseite des Briefes — dieselben Bausteine, gefiltert nach ihrer Seitenangabe. */
  letterFollowUpHtml: string;
  /** Musterwerte für die token-sichere Bearbeitung in der Vorschau. */
  templateValues?: Record<string, string> | null;
};

/** Der ausgelieferte mQorva-Standard, wie ihn auch das Backend kennt. */
export const SHIPPED_DEFAULT: DocumentAppearanceDto = {
  id: null,
  name: null,
  studioIds: [],
  channel: ["Letter", "Email"],
  isActive: true,
  letterBlocks: defaultBlocks(),
  emailBlocks: defaultBlocks(),
  emailAppearance: { footerOutsideContent: true },
  // Ein neuer Brief steht von Anfang an nach DIN 5008: Die Oberkante des Anschriftfelds bei 45 mm
  // ist der Wert, an dem das Kuvertfenster sitzt. Ohne Vorgabe liefe das Feld im Textfluss mit und
  // wanderte mit jeder Logohöhe — bestehende Formate behalten ihren Stand.
  pageAppearance: { addressFieldTopMm: 45 },
  appliesToWhatsApp: false,
  whatsAppAppearance: null,
};

export function listDocumentAppearances(api: ApiClient, withStudioScope: (url: string) => string = (url) => url) {
  return api.get<DocumentAppearanceDto[]>(withStudioScope("/api/v1/document-appearance"));
}

export function saveDocumentAppearance(api: ApiClient, dto: DocumentAppearanceDto) {
  return api.put<DocumentAppearanceDto>("/api/v1/document-appearance", dto);
}

export function deleteDocumentAppearance(api: ApiClient, id: string) {
  return api.del(`/api/v1/document-appearance/${id}`);
}

/** Brief und E-Mail aus Musterdaten mit den ungespeicherten Werten. */
export function previewDocumentAppearance(
  api: ApiClient,
  dto: DocumentAppearanceDto,
  language: string,
  sampleTableChannels: string[] = [],
) {
  const tableChannels = sampleTableChannels.length > 0
    ? `&sampleTableChannels=${encodeURIComponent(sampleTableChannels.join(","))}`
    : "";
  return api.post<DocumentAppearancePreviewDto>(
    `/api/v1/document-appearance/preview?language=${encodeURIComponent(language)}${tableChannels}`,
    dto
  );
}

/** Das echte PDF — die HTML-Vorschau zeigt die Struktur, das PDF das Ergebnis. */
export function previewDocumentAppearancePdf(api: ApiClient, dto: DocumentAppearanceDto, language: string) {
  return api.blob(`/api/v1/document-appearance/preview.pdf?language=${encodeURIComponent(language)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(dto),
  });
}
